import type { PayoutBalanceResponse, PayoutFleetEntry } from "@nova-ai/shared"
import { convertToEur, type ExchangeRates } from "./money"

/**
 * The Payouts page's block order: biggest channel first, by **lifetime paid**
 * converted to EUR.
 *
 * Converted because the fleet sells in EUR, CZK and PLN — a raw amount would rank
 * a PLN channel above a larger EUR one on the strength of its exchange rate.
 *
 * Lifetime paid rather than what is owed right now: it measures how big a channel
 * is, and it barely moves, so a block stays where it was yesterday. Ranking on
 * `payable` would reshuffle the page three times a month, because a payout landing
 * drops that marketplace to zero — and a block that moves with the money is a
 * block you cannot find twice.
 *
 * A marketplace with no snapshot yet, or one whose currency has no known rate,
 * cannot be compared on money at all. Those sort **last, by name**, rather than
 * being silently treated as zero and mixed in among genuinely small channels.
 * Ties break by name too, so the order is total and stable across renders.
 */
export function byLifetimePaid(
  balances: Map<string, PayoutBalanceResponse>,
  rates: ExchangeRates | undefined
): (a: PayoutFleetEntry, b: PayoutFleetEntry) => number {
  const lifetimeEur = (entry: PayoutFleetEntry): number | null => {
    const balance = balances.get(entry.marketplace)?.balance
    if (!balance) {
      return null
    }
    // Amounts are exact decimal strings everywhere they are DISPLAYED. A float is
    // fine for deciding an order — nothing here reaches the DOM.
    const paid = Number(balance.paid)
    return Number.isFinite(paid) ? convertToEur(paid, balance.currency, rates) : null
  }
  return (a, b) => {
    const left = lifetimeEur(a)
    const right = lifetimeEur(b)
    if (left === null || right === null) {
      if (left === right) {
        return a.marketplace.localeCompare(b.marketplace)
      }
      return left === null ? 1 : -1
    }
    return right - left || a.marketplace.localeCompare(b.marketplace)
  }
}
